import { EnvironmentService } from '@/services/Environment';
import { UserRole } from '@/types';
import { parseJwtPayload } from '@/utils/parse-jwt-payload';

class AnalyticsService {
  /**
   * User has accepted the Partner Portal invitation from the email and has finalized the account creation
   */
  public acceptInvitation(userId: string) {
    try {
      AnalyticsService.trackEvent('invitation_accept', {
        user_id: userId,
      });
    } catch {
      AnalyticsService.log('invitation_accept', {
        user_id: userId,
      });
    }
  }

  /**
   * User has successfully created a new lead.
   * NOTE: for now, you can always pass isFirstLead param as false
   */
  public createNewLead(isFirstLead = false) {
    const { userId, role, companyId, companyName } = parseJwtPayload();
    const params = {
      lifecycle_stage: isFirstLead ? 'activation' : 'retention',
      user_id: userId,
      user_role: AnalyticsService.getUserRole(role),
      partner_account_id: companyId,
      partner_account_name: companyName,
    };

    try {
      AnalyticsService.trackEvent('proposal_request', params);
    } catch {
      AnalyticsService.log('proposal_request', params);
    }
  }

  /**
   * User has successfully requested a new proposal for an opportunity
   */
  public requestNewProposalForOpportunity(opportunityId: string) {
    const { userId, role, companyId, companyName } = parseJwtPayload();
    const params = {
      opportunity_id: opportunityId,
      user_id: userId,
      user_role: AnalyticsService.getUserRole(role),
      partner_account_id: companyId,
      partner_account_name: companyName,
    };

    try {
      AnalyticsService.trackEvent('revision_request', params);
    } catch {
      AnalyticsService.log('revision_request', params);
    }
  }

  /**
   * User has successfully accepted a proposal for an opportunity
   */
  public acceptProposalForOpportunity(opportunityId: string, proposalId: string) {
    const { userId, role, companyId, companyName } = parseJwtPayload();
    const params = {
      opportunity_id: opportunityId,
      proposal_id: proposalId,
      user_id: userId,
      user_role: AnalyticsService.getUserRole(role),
      partner_account_id: companyId,
      partner_account_name: companyName,
    };

    try {
      AnalyticsService.trackEvent('proposal_accept', params);
    } catch {
      AnalyticsService.log('proposal_accept', params);
    }
  }

  /**
   * User has successfully closed proposal for an opportunity as 'Closed Lost'
   */
  public closeProposalForOpportunityAsLost(opportunityId: string, proposalId: string) {
    const { userId, role, companyId, companyName } = parseJwtPayload();
    const params = {
      opportunity_id: opportunityId,
      proposal_id: proposalId,
      user_id: userId,
      user_role: AnalyticsService.getUserRole(role),
      partner_account_id: companyId,
      partner_account_name: companyName,
    };

    try {
      AnalyticsService.trackEvent('proposal_close_lost', params);
    } catch {
      AnalyticsService.log('proposal_close_lost', params);
    }
  }

  /**
   * User has successfully created a new order.
   * NOTE: leave opportunityId and proposalId as nulls if they are not present
   */
  public createOrder(
    opportunityId: string | null,
    proposalId: string | null,
    isDirectOrder: boolean,
  ) {
    const { userId, role, companyId, companyName } = parseJwtPayload();
    const params = {
      opportunity_id: opportunityId,
      proposal_id: proposalId,
      order_type: isDirectOrder ? 'Direct' : 'Design',
      user_id: userId,
      user_role: AnalyticsService.getUserRole(role),
      partner_account_id: companyId,
      partner_account_name: companyName,
    };

    try {
      AnalyticsService.trackEvent('order_submit', params);
    } catch {
      AnalyticsService.log('order_submit', params);
    }
  }

  /**
   * User has successfully logged in
   */
  public logIn() {
    const { assistedByAdmin, assistingAdminId, companyId, companyName, role, userId } =
      parseJwtPayload();
    const params = {
      user_id: userId,
      user_role: AnalyticsService.getUserRole(role),
      partner_account_id: companyId,
      partner_account_name: companyName,
      is_assisted_by_admin: assistedByAdmin,
      assisting_user_id: assistingAdminId,
    };

    try {
      AnalyticsService.trackEvent('user_login', params);
    } catch {
      AnalyticsService.log('user_login', params);
    }
  }

  /**
   * The application has successfully fetched the user and his/her company information
   */
  public fetchUserInfo(
    userId: string,
    userRole: UserRole,
    companyId: string,
    companyName: string,
    assistedByAdmin: boolean,
    assistingAdminId: string,
    companyState: string,
  ) {
    const params = {
      user_id: userId,
      user_role: AnalyticsService.getUserRole(userRole),
      partner_account_id: companyId,
      partner_account_name: companyName,
      is_assisted_by_admin: assistedByAdmin,
      assisting_user_id: assistingAdminId,
      user_company_state: companyState,
    };

    try {
      AnalyticsService.trackEvent('associate_user', params);
      // this is sent on purpose
      AnalyticsService.trackEvent(null, {
        user_id: userId,
        crm_id: userId,
      });
    } catch {
      AnalyticsService.log('associate_user', params);
      AnalyticsService.log(null, {
        user_id: userId,
        crm_id: userId,
      });
    }
  }

  /**
   * User has clicked a specific button
   */
  public trackClickEvent(event: string | null, params: object = {}) {
    try {
      AnalyticsService.trackEvent(event, params);
    } catch {
      AnalyticsService.log(event, params);
    }
  }

  /**
   * User has clicked company metrics timeframe
   */
  public trackCompanyMetricsEvent(event: string | null, option: string) {
    const { userId, companyId, companyName } = parseJwtPayload();

    const params = {
      partner_account_id: companyId,
      partner_account_name: companyName,
      range_selector: option,
      timestamp: Date.now(),
      user_id: userId,
    };

    try {
      AnalyticsService.trackEvent(event, params);
    } catch {
      AnalyticsService.log(event, params);
    }
  }

  private static trackEvent(event: string | null, params: object): void {
    const dataLayer = window.dataLayer || [];

    dataLayer.push({
      event,
      params,
    });

    AnalyticsService.log(event, params, 'success');
  }

  private static log(
    event: string | null,
    params: object,
    type: 'fail' | 'success' = 'fail',
  ): void {
    if (!EnvironmentService.withLogs) {
      return void 0;
    }
    const messageType = type === 'fail' ? 'error' : 'info';

    console.groupCollapsed(`Google Analytics event "${event ?? 'noname'}"`);
    console[messageType](params);
    console.groupEnd();
  }

  private static getUserRole(userRole: UserRole | string): string {
    if (typeof userRole === 'string') {
      return userRole;
    }

    return userRole === UserRole.Admin ? 'Admin' : 'Sales';
  }
}

export const GoogleAnalytics = new AnalyticsService();
